import { cn } from "@/lib/utils";

/**
 * Skeleton — a single pill/block with a smooth left-to-right shimmer.
 * Shimmer keyframes live in index.css so they respect prefers-reduced-motion
 * via the global media query fallback.
 */
export function Skeleton({ className, testId }) {
  return (
    <div
      className={cn(
        "mb-skeleton relative overflow-hidden rounded-lg bg-gray-100",
        className
      )}
      data-testid={testId}
      aria-hidden="true"
    />
  );
}

/** Card-shaped skeleton mimicking inventory/requirement/match/pipeline cards. */
export function SkeletonCard({ className, lines = 3, testId }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card",
        className
      )}
      data-testid={testId}
      aria-hidden="true"
    >
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
        </div>
      </div>
      <Skeleton className="h-6 w-1/3" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-3 w-full" />
      ))}
      <div className="mt-2 flex items-center gap-2">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-20" />
        <Skeleton className="ml-auto h-3 w-16" />
      </div>
    </div>
  );
}

/**
 * SkeletonGrid — Nx SkeletonCards on a responsive grid, matching the
 * inventory/requirements/pipeline layouts closely enough that content
 * doesn't jump on load.
 */
export function SkeletonGrid({ count = 6, columns = "md:grid-cols-2 lg:grid-cols-3", testId }) {
  return (
    <ul
      className={cn("grid grid-cols-1 gap-6 md:gap-8", columns)}
      data-testid={testId}
    >
      {Array.from({ length: count }).map((_, i) => (
        <li key={i}>
          <SkeletonCard />
        </li>
      ))}
    </ul>
  );
}

/** Wide skeleton row for match cards (score badge + two panels). */
export function SkeletonMatchRow({ testId }) {
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card"
      data-testid={testId}
    >
      <div className="flex flex-col gap-6 md:flex-row">
        <Skeleton className="h-24 w-full md:w-40" />
        <div className="grid flex-1 grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-3 w-full" />
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Row skeleton for admin queue / transactions list. */
export function SkeletonListRow({ testId }) {
  return (
    <div
      className="grid grid-cols-1 items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-mb-card md:grid-cols-[160px,1fr,auto]"
      data-testid={testId}
    >
      <Skeleton className="h-24 w-full rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-10 w-32" />
    </div>
  );
}
